import { useRef } from 'react'
import { Box, Flex, Text, VStack } from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'

import { GRAPHICS_QUERY_KEY, useCreateGraphic } from '@/api/graphics'
import IconUpload from '@/components/icons/IconUpload'
import Button from '@/components/ui/Button'
import Modal from '@/components/ui/Modal'
import useMultiFileUpload from '@/hooks/useMultiFileUpload'
import { sanitizeFileName } from '@/utils/validation'

import FileRow from './file-row'

interface UploadGraphicsModalProps {
  isOpen: boolean
  onClose: () => void
  collectionId: string
}

const ACCEPTED_TYPES = '.png,.jpeg,.jpg'

export default function UploadGraphicsModal({
  isOpen,
  onClose,
  collectionId
}: UploadGraphicsModalProps) {
  const { files, count, flags, addFiles, removeFile, retryFile, reset, abort } =
    useMultiFileUpload()

  const createGraphic = useCreateGraphic()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const hasUploadedFiles = files.some((f) => f.uploaded && f.imageUrl)
  const isConfirmDisabled = flags.isUploading || !hasUploadedFiles

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files ?? [])
    if (selectedFiles.length) {
      addFiles(selectedFiles)
    }
    e.target.value = ''
  }

  const handleConfirm = async () => {
    const uploaded = files.filter((f) => f.uploaded && f.imageUrl)

    await Promise.all(
      uploaded.map((entry) =>
        createGraphic.mutateAsync({
          name: sanitizeFileName(entry.file.name),
          imageUrl: entry.imageUrl!,
          collectionId
        })
      )
    )

    queryClient.invalidateQueries({ queryKey: [GRAPHICS_QUERY_KEY] })
    reset()
    onClose()
  }

  const handleClose = () => {
    if (flags.isUploading) {
      abort()
    }
    reset()
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Upload Graphics">
      <Flex direction="column" gap={5}>
        <Flex direction="column" gap="10px">
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            multiple
            onChange={handleFilesSelected}
            style={{ display: 'none' }}
          />
          <Flex
            align="center"
            justify="center"
            h="208px"
            w="full"
            bg="lightGray.400"
            borderWidth="1px"
            borderStyle="dashed"
            borderColor="brand.600"
            borderRadius="8px"
            cursor="pointer"
            overflow="hidden"
            onClick={() => fileInputRef.current?.click()}
          >
            <VStack gap="10px">
              <IconUpload w="40px" h="40px" />
              <VStack gap={1}>
                <Text
                  textStyle="bodySmall"
                  fontWeight={500}
                  color="brand.600"
                  textAlign="center"
                >
                  Drag & Drop or Click to Upload
                </Text>
                <Text
                  textStyle="bodySmall"
                  fontWeight={500}
                  color="brand.600"
                  textAlign="center"
                >
                  PNG, JPEG
                </Text>
              </VStack>
            </VStack>
          </Flex>
          <Text textStyle="bodySuperSmall" color="secondaryDarkGray.600">
            {flags.hasFiles
              ? flags.allDone
                ? `Completed: ${count.completed} of ${count.total}`
                : `Uploading: ${count.completed} of ${count.total}`
              : 'Not uploaded'}
          </Text>
        </Flex>

        {flags.hasFiles && (
          <Flex
            direction="column"
            gap="26px"
            maxH="300px"
            overflowY="auto"
            css={{
              '&::-webkit-scrollbar': { width: '3px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: '#A0AEC0',
                borderRadius: '8px'
              }
            }}
          >
            {files.map((entry) => (
              <FileRow
                key={entry.id}
                id={entry.id}
                name={entry.file.name}
                status={entry.status}
                onRemove={removeFile}
                onRetry={retryFile}
              />
            ))}
          </Flex>
        )}
      </Flex>

      <Box borderTop="1px solid" borderColor="border.subtle" pt={5} mt={5}>
        <Button
          w="full"
          variant="primary"
          disabled={isConfirmDisabled}
          onClick={handleConfirm}
        >
          Confirm
        </Button>
      </Box>
    </Modal>
  )
}
